import { ReactNode, Suspense, lazy } from 'react';
import { Link } from 'react-router';
import { useSnapshot } from 'valtio';

import * as authStore from '@cartwhl/common/stores/authStore';
import * as userStore from '@cartwhl/common/stores/userStore';
import { getAppletsUrl } from '@cartwhl/common/utils';
import { getHomepageUrl } from '@cartwhl/common/utils/getHomepageUrl';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';
import GoButton from '@cartwhl/ui/input/GoButton';
import HeaderSubMenu from '@cartwhl/ui/layout/HeaderSubMenu';
import MobileMenu from '@cartwhl/ui/layout/MobileMenu';
import {
  NewAppsBanner,
  useNewAppsBanner,
} from '@cartwhl/ui/layout/NewAppsBanner';
import UserMenu from '@cartwhl/ui/organisms/UserMenu';
import FrostedGlass from '@cartwhl/ui/utilities/FrostedGlass';

const AnimatedLogo = lazy(() => import('@cartwhl/ui/atoms/AnimatedLogo'));

const pluginsLink = { label: 'Plugins', path: '/plugins' };

const navLinks = [
  { label: 'Data', path: '/data' },
  // TODO: Re-enable Blog when ready
  // { label: 'Blog', path: '/blog' },
  { label: 'Company', path: '/company' },
];

const pricingMenuItems = [
  {
    title: 'Plans',
    href: '/pricing',
  },
  {
    title: 'How credits work',
    href: '/pricing#pricing-credits-explainer',
  },
];

interface ProductMenuItem {
  title: string;
  subtitle: string;
  href: string;
  comingSoonText: string;
}

interface ProductMenuSection {
  title: string;
  items: ProductMenuItem[];
}

const productMenuSections: ProductMenuSection[] = [
  {
    title: 'Standalone apps',
    items: [
      {
        title: 'Comic',
        subtitle: 'AI performance capture',
        href: '/product/comic-ai-video-motion-capture',
        comingSoonText: '',
      },
      {
        title: 'Swing',
        subtitle: 'Text-to-motion',
        href: '/product/swing-motion-generation',
        comingSoonText: '',
      },
      {
        title: 'API',
        subtitle: 'Access at scale',
        href: '/product/api-access',
        comingSoonText: '',
      },
      {
        title: 'Mascot',
        subtitle: 'Video character replacement',
        href: '/product/mascot',
        comingSoonText: '',
      },
      {
        title: 'Cartographer',
        subtitle: 'Universal motion retargeting',
        href: '/product/cartographer',
        comingSoonText: '',
      },
      {
        title: 'Motion Editor',
        subtitle: 'Smart motion editing',
        href: '/product/motion-editor',
        comingSoonText: '',
      },
    ],
  },
  {
    title: 'Canvas only',
    items: [
      {
        title: 'Character Generator',
        subtitle: 'Reference-to-rigged 3D',
        href: '/product/character-generation',
        comingSoonText: '',
      },
      {
        title: 'Pose Mode',
        subtitle: 'Pose-to-render',
        href: '/product/pose-render',
        comingSoonText: '',
      },
      {
        title: 'Motion Library',
        subtitle: 'Production-ready motion',
        href: '/product/motion-library',
        comingSoonText: '',
      },
    ],
  },
];

const learnMenuItems = [
  {
    title: 'Canvas Docs',
    subtitle: 'Learn how to use Canvas',
    href: 'https://docs.getcartwheel.com/docs/getting-started/introduction',
    external: true,
  },
  {
    title: 'API Docs',
    subtitle: 'API reference & guides',
    href: 'https://api-docs.getcartwheel.com/',
    external: true,
  },
];

// Check if we're on the homepage origin (client-side nav works)
// vs another app like applets (need full page navigation)
const isOnHomepage = getHomepageUrl('/') === `${window.location.origin}/`;

interface HomepageLinkProps {
  to: string;
  className?: string;
  children: ReactNode;
}

function HomepageLink({ to, className, children }: HomepageLinkProps) {
  if (isOnHomepage) {
    return (
      <Link to={to} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <a href={getHomepageUrl(to)} className={className}>
      {children}
    </a>
  );
}

interface HeaderProps {
  onComingSoonClick?: (slug?: string) => void;
}

export default function Header({ onComingSoonClick }: HeaderProps = {}) {
  const { authStatus } = useSnapshot(authStore.state);
  const { theme } = useSnapshot(userStore.state);
  const { showBanner: showNewAppsBanner, dismissBanner: dismissNewAppsBanner } =
    useNewAppsBanner();
  const isSignedIn = authStatus === 'user';

  // Header is 128px with nav centered. Negative top value ensures nav sits at 16px from viewport when stuck
  return (
    <header
      className={`sticky z-50 flex flex-col ${
        showNewAppsBanner ? 'top-0' : 'top-0 lg:top-[-16px]'
      }`}
    >
      {showNewAppsBanner ? (
        <NewAppsBanner
          onDismiss={dismissNewAppsBanner}
          comicHref="/product/comic-ai-video-motion-capture"
          comicLinkAsRouter
        />
      ) : null}

      <div
        className={`flex h-20 items-center justify-center px-2 lg:px-5 ${
          showNewAppsBanner ? 'lg:h-24' : 'lg:h-[128px]'
        }`}
      >
        <FrostedGlass className="container rounded-lg lg:rounded-full">
          <div className="flex h-16 items-center justify-between">
            <HomepageLink to="/" className="flex items-center">
              <Suspense fallback={null}>
                <AnimatedLogo theme={theme ?? 'light'} />
              </Suspense>
            </HomepageLink>

            {/* Mobile menu */}
            <MobileMenu
              productSections={productMenuSections}
              pricingItems={pricingMenuItems}
              learnItems={learnMenuItems}
              onComingSoonClick={onComingSoonClick}
            />

            {/* Desktop navigation */}
            <nav className="hidden items-center gap-6 lg:flex">
              <HeaderSubMenu
                contentClassName="overflow-hidden p-0"
                trigger={
                  <Text
                    variant="web/body/medium/md"
                    className="transition-opacity hover:opacity-80"
                  >
                    Product
                  </Text>
                }
              >
                <HeaderSubMenu.SectionGrid>
                  {productMenuSections.map((section) => (
                    <HeaderSubMenu.Section
                      key={section.title}
                      title={section.title}
                    >
                      {section.items.map((item) => (
                        <HeaderSubMenu.Item
                          key={item.href}
                          title={item.title}
                          subtitle={item.subtitle}
                          href={item.href}
                          comingSoon={item.comingSoonText !== ''}
                          comingSoonText={item.comingSoonText}
                          onComingSoonClick={
                            item.comingSoonText !== ''
                              ? onComingSoonClick
                              : undefined
                          }
                        />
                      ))}
                    </HeaderSubMenu.Section>
                  ))}
                </HeaderSubMenu.SectionGrid>
              </HeaderSubMenu>

              <HomepageLink
                to={pluginsLink.path}
                className="transition-opacity hover:opacity-80"
              >
                <Text variant="web/body/medium/md">{pluginsLink.label}</Text>
              </HomepageLink>

              {navLinks.slice(0, 1).map((link) => (
                <HomepageLink
                  key={link.label}
                  to={link.path}
                  className="transition-opacity hover:opacity-80"
                >
                  <Text variant="web/body/medium/md">{link.label}</Text>
                </HomepageLink>
              ))}

              <HeaderSubMenu
                trigger={
                  <Text
                    variant="web/body/medium/md"
                    className="transition-opacity hover:opacity-80"
                  >
                    Pricing
                  </Text>
                }
              >
                <HeaderSubMenu.Grid columns={1}>
                  {pricingMenuItems.map((item) => (
                    <HeaderSubMenu.Item
                      key={item.href}
                      title={item.title}
                      href={item.href}
                    />
                  ))}
                </HeaderSubMenu.Grid>
              </HeaderSubMenu>

              {navLinks.slice(1).map((link) => (
                <HomepageLink
                  key={link.label}
                  to={link.path}
                  className="transition-opacity hover:opacity-80"
                >
                  <Text variant="web/body/medium/md">{link.label}</Text>
                </HomepageLink>
              ))}

              <HeaderSubMenu
                trigger={
                  <Text
                    variant="web/body/medium/md"
                    className="transition-opacity hover:opacity-80"
                  >
                    Learn
                  </Text>
                }
              >
                <HeaderSubMenu.Grid columns={1}>
                  {learnMenuItems.map((item) => (
                    <HeaderSubMenu.Item
                      key={item.href}
                      title={item.title}
                      subtitle={item.subtitle}
                      href={item.href}
                      external={item.external}
                    />
                  ))}
                </HeaderSubMenu.Grid>
              </HeaderSubMenu>

              <div className="flex items-center gap-2">
                {!isSignedIn && (
                  <HomepageLink to="/login">
                    <Button variant="secondary">Sign in</Button>
                  </HomepageLink>
                )}
                {isSignedIn ? (
                  <a href={getAppletsUrl()}>
                    <GoButton variant="animate">Launch Cartwheel</GoButton>
                  </a>
                ) : (
                  <HomepageLink to="/pricing">
                    <GoButton variant="animate">Free trial</GoButton>
                  </HomepageLink>
                )}
                {isSignedIn && <UserMenu />}
              </div>
            </nav>
          </div>
        </FrostedGlass>
      </div>
    </header>
  );
}
